import React, { useState, useEffect } from "react";

const REFLECTION_PROMPTS = [
  "What was the one moment today where you felt most like yourself?",
  "What did you avoid today that you already know you need to face?",
  "If today had a title, what would it be?",
  "What conversation are you still carrying from today?",
  "What would you do differently if today started over?",
  "What small thing happened that you almost didn't notice?",
  "Where did you feel resistance today — and what was underneath it?",
];

export default function EveningReflection({ onClose }) {
  const [prompt, setPrompt] = useState("");
  const [response, setResponse] = useState("");
  const [saved, setSaved] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const dayIndex = new Date().getDay();
    setPrompt(REFLECTION_PROMPTS[dayIndex % REFLECTION_PROMPTS.length]);
  }, []);

  useEffect(() => {
    if (!started) return;
    const interval = setInterval(() => setElapsed((p) => p + 1), 1000);
    return () => clearInterval(interval);
  }, [started]);

  const formatTime = (s) =>
    Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");

  const handleSave = () => {
    if (!response.trim()) return;
    const today = new Date().toDateString();
    const existing = JSON.parse(localStorage.getItem("eveningReflections") || "[]");
    existing.push({ date: today, prompt, response, duration: elapsed });
    localStorage.setItem("eveningReflections", JSON.stringify(existing.slice(-30)));
    setSaved(true);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.moonIcon}>🌙</span>
            <span style={styles.title}>Evening Reflection</span>
          </div>
          <div style={styles.headerRight}>
            {started && !saved && (
              <span style={styles.timer}>⏱ {formatTime(elapsed)}</span>
            )}
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={styles.promptBox}>
          <p style={styles.promptLabel}>Tonight's question</p>
          <p style={styles.promptText}>{prompt}</p>
        </div>

        {!saved ? (
          <>
            <textarea
              style={styles.textarea}
              placeholder="Take your time. No one is reading this but you."
              value={response}
              onChange={(e) => {
                setResponse(e.target.value);
                if (!started && e.target.value.length > 0) setStarted(true);
              }}
              rows={6}
              autoFocus
            />
            <div style={styles.actions}>
              <span style={styles.wordCount}>
                {response.trim() ? response.trim().split(/\s+/).length + " words" : ""}
              </span>
              <button
                style={{
                  ...styles.saveBtn,
                  opacity: response.trim() ? 1 : 0.4,
                  cursor: response.trim() ? "pointer" : "default",
                }}
                onClick={handleSave}
                disabled={!response.trim()}
              >
                Save reflection
              </button>
            </div>
          </>
        ) : (
          <div style={styles.savedState}>
            <div style={styles.savedIcon}>✦</div>
            <p style={styles.savedTitle}>Reflection saved.</p>
            <p style={styles.savedSub}>
              {elapsed > 0 ? formatTime(elapsed) + " of honest thought." : "Good night."}
            </p>
            <button style={styles.doneBtn} onClick={onClose}>
              Done for today
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(8,6,18,0.85)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: "20px", backdropFilter: "blur(4px)",
  },
  card: {
    background: "linear-gradient(135deg, #0d0b1a 0%, #13102b 100%)",
    border: "1px solid rgba(139,92,246,0.25)", borderRadius: "16px",
    padding: "28px", width: "100%", maxWidth: "520px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "10px" },
  headerRight: { display: "flex", alignItems: "center", gap: "12px" },
  moonIcon: { fontSize: "20px" },
  title: { fontSize: "17px", fontWeight: "600", color: "#e2d9f3" },
  timer: {
    fontSize: "12px", fontWeight: "600", color: "#8b5cf6",
    background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.3)",
    borderRadius: "20px", padding: "3px 10px",
  },
  closeBtn: { background: "none", border: "none", color: "#6b7280", fontSize: "16px", cursor: "pointer", padding: "4px" },
  promptBox: {
    background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)",
    borderRadius: "12px", padding: "18px 20px", marginBottom: "20px",
  },
  promptLabel: { fontSize: "11px", color: "#8b5cf6", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px 0" },
  promptText: { fontSize: "16px", color: "#e2d9f3", lineHeight: "1.6", margin: 0, fontStyle: "italic" },
  textarea: {
    width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px", padding: "14px", color: "#e2d9f3", fontSize: "15px", lineHeight: "1.65",
    resize: "vertical", outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
  actions: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px" },
  wordCount: { fontSize: "12px", color: "#6b7280" },
  saveBtn: {
    background: "linear-gradient(135deg, #7c3aed, #5b21b6)", color: "#fff", border: "none",
    borderRadius: "8px", padding: "10px 20px", fontSize: "14px", fontWeight: "600", cursor: "pointer",
  },
  savedState: { textAlign: "center", padding: "20px 0" },
  savedIcon: { fontSize: "28px", color: "#8b5cf6", marginBottom: "12px" },
  savedTitle: { fontSize: "20px", fontWeight: "700", color: "#e2d9f3", margin: "0 0 8px" },
  savedSub: { fontSize: "14px", color: "#9ca3af", margin: "0 0 24px" },
  doneBtn: {
    background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.4)",
    color: "#a78bfa", borderRadius: "8px", padding: "10px 24px",
    fontSize: "14px", fontWeight: "600", cursor: "pointer",
  },
};
